import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Download, MapPin, Monitor, Smartphone } from "lucide-react";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import {
  breakLabel,
  STATE_META,
  formatDateTime,
  formatDuration,
  formatTime,
  isoDate,
} from "../../../utils/attendance";
import { formatCoords, mapsUrl } from "../../../utils/geolocation";

// Shift-by-shift records with a per-agent roll-up over the same range. This is
// the screen payroll and WFM actually export from, so the totals shown here are
// the server's, not recomputed in the browser.

const ROWS_PER_PAGE = 25;
const SUMMARY_PER_PAGE = 10;

const toCsv = (rows) => {
  const head = [
    "Date",
    "Agent",
    "User ID",
    "Clock In",
    "Clock Out",
    "Breaks",
    "Break Time (min)",
    "Productive (min)",
    "Total (min)",
    "Late (min)",
    "Status",
    "Note",
    "Clock In Location",
    "Clock Out Location",
    "Clock In Device",
    "Clock Out Device",
  ];
  const body = rows.map((r) => [
    r.date,
    r.agentName,
    r.agentUserId,
    r.clockInAt ? new Date(r.clockInAt).toLocaleString() : "",
    r.clockOutAt ? new Date(r.clockOutAt).toLocaleString() : "",
    r.breaks.length,
    Math.round(r.totals.breakMs / 60000),
    Math.round(r.totals.productiveMs / 60000),
    Math.round(r.totals.workedMs / 60000),
    Math.round((r.lateBy || 0) / 60000),
    r.status,
    (r.clockOutNote || "").replace(/"/g, "'"),
    r.clockInLocation ? `${r.clockInLocation.lat}, ${r.clockInLocation.lng}` : "",
    r.clockOutLocation ? `${r.clockOutLocation.lat}, ${r.clockOutLocation.lng}` : "",
    r.clockInDevice?.label || "",
    r.clockOutDevice?.label || "",
  ]);
  return [head, ...body]
    .map((line) => line.map((cell) => `"${String(cell ?? "")}"`).join(","))
    .join("\n");
};

/** Small device icon after a punch time; hover shows "Chrome 130 · Windows 11 · Desktop". */
const DeviceIcon = ({ device, verb }) => {
  if (!device?.label) return null;
  const Icon = device.deviceType === "Desktop" ? Monitor : Smartphone;
  return (
    <Tooltip title={`${verb} from ${device.label}`}>
      <span style={{ marginLeft: 4, verticalAlign: "middle", color: "#6b7280", display: "inline-flex" }}>
        <Icon size={13} />
      </span>
    </Tooltip>
  );
};

export default function AttendanceRecords() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryPages, setSummaryPages] = useState(1);
  const [summaryTotal, setSummaryTotal] = useState(0);
  const [summaryTotals, setSummaryTotals] = useState(null);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({
    from: isoDate(6),
    to: isoDate(0),
    agentId: "",
    status: "",
  });

  // The picker needs every agent, so it asks for the slim unpaginated list
  // rather than a page of full records.
  useEffect(() => {
    axiosInstance
      .get(apiEndPointsConfig.agents, { params: { brief: true } })
      .then(({ data }) => setAgents(data?.data || []))
      .catch(() => setAgents([]));
  }, []);

  // The two tables page independently, so they load independently -- stepping
  // through shifts should not re-fetch the roll-up and vice versa.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(apiEndPointsConfig.attendanceRecords, {
        params: { ...filters, page, limit: ROWS_PER_PAGE },
      });
      setRows(data?.data || []);
      setPages(data?.pagination?.pages || 1);
      setTotal(data?.pagination?.total || 0);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load attendance records");
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const { data } = await axiosInstance.get(apiEndPointsConfig.attendanceSummary, {
        params: {
          from: filters.from,
          to: filters.to,
          agentId: filters.agentId || undefined,
          page: summaryPage,
          limit: SUMMARY_PER_PAGE,
        },
      });
      setSummary(data?.data || []);
      setSummaryPages(data?.pagination?.pages || 1);
      setSummaryTotal(data?.pagination?.total || 0);
      setSummaryTotals(data?.totals || null);
    } catch (err) {
      setSummary([]);
    } finally {
      setSummaryLoading(false);
    }
  }, [filters.from, filters.to, filters.agentId, summaryPage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Changing the range invalidates the roll-up's page number.
  useEffect(() => {
    setSummaryPage(1);
  }, [filters.from, filters.to, filters.agentId]);

  const setFilter = (key, value) => {
    setPage(1);
    setFilters((f) => ({ ...f, [key]: value }));
  };

  // Exports everything the filters match, not the page on screen. Now that
  // paging is server-side the visible rows are a slice, and a CSV that quietly
  // contained a slice would be worse than no CSV at all.
  const exportCsv = async () => {
    setExporting(true);
    setError("");
    try {
      const all = [];
      const MAX_PAGES = 100; // 20k rows, a guard rather than an expected limit
      for (let p = 1; p <= MAX_PAGES; p += 1) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await axiosInstance.get(apiEndPointsConfig.attendanceRecords, {
          params: { ...filters, page: p, limit: 200 },
        });
        all.push(...(data?.data || []));
        if (!data?.pagination?.hasMore) break;
      }

      const blob = new Blob([toCsv(all)], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance-${filters.from}-to-${filters.to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not build the export");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          boxShadow: "none",
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          type="date"
          size="small"
          label="From"
          InputLabelProps={{ shrink: true }}
          value={filters.from}
          onChange={(e) => setFilter("from", e.target.value)}
        />
        <TextField
          type="date"
          size="small"
          label="To"
          InputLabelProps={{ shrink: true }}
          value={filters.to}
          onChange={(e) => setFilter("to", e.target.value)}
        />
        <TextField
          select
          size="small"
          label="Agent"
          value={filters.agentId}
          onChange={(e) => setFilter("agentId", e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All agents</MenuItem>
          {agents.map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {a.name || a.userid}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={filters.status}
          onChange={(e) => setFilter("status", e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">Any</MenuItem>
          <MenuItem value="clocked_in">Clocked In</MenuItem>
          <MenuItem value="on_break">On Break</MenuItem>
          <MenuItem value="clocked_out">Clocked Out</MenuItem>
        </TextField>

        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={<Download size={16} />}
          onClick={exportCsv}
          disabled={!rows.length || exporting}
          sx={{ color: "#0C237B" }}
        >
          {exporting ? "Preparing..." : `Export CSV${total ? ` (${total})` : ""}`}
        </Button>
      </Paper>

      {/* ── Per-agent roll-up for the range ──────────────────────────────── */}
      <Paper
        sx={{ borderRadius: "12px", border: "1px solid #e5e7eb", boxShadow: "none", mb: 3, overflow: "hidden" }}
      >
        <Typography sx={{ fontWeight: 700, color: "#374151", p: 2, pb: 1 }}>
          Summary · {filters.from} to {filters.to}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, color: "#6b7280", bgcolor: "#f9fafb" } }}>
                <TableCell>Agent</TableCell>
                <TableCell align="right">Days</TableCell>
                <TableCell align="right">Shifts</TableCell>
                <TableCell align="right">Breaks</TableCell>
                <TableCell align="right">Break Time</TableCell>
                <TableCell align="right">Productive</TableCell>
                <TableCell align="right">Avg / Day</TableCell>
                <TableCell align="right">Late</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!summary.length && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: "#9ca3af" }}>
                    {summaryLoading ? "Loading..." : "Nothing recorded in this period."}
                  </TableCell>
                </TableRow>
              )}
              {summary.map((s) => (
                <TableRow key={s.agentId} hover>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {s.agentName || s.agentUserId}
                  </TableCell>
                  <TableCell align="right">{s.daysPresent}</TableCell>
                  <TableCell align="right">{s.shifts}</TableCell>
                  <TableCell align="right">{s.breakCount}</TableCell>
                  <TableCell align="right" sx={{ color: "#d97706" }}>
                    {formatDuration(s.breakMs)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "#059669" }}>
                    {formatDuration(s.productiveMs)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: "#6b7280" }}>
                    {formatDuration(s.avgProductiveMsPerDay)}
                  </TableCell>
                  <TableCell align="right">
                    {s.lateCount ? (
                      <Chip
                        size="small"
                        label={s.lateCount}
                        sx={{ bgcolor: "#fef2f2", color: "#dc2626", fontWeight: 700, height: 20 }}
                      />
                    ) : (
                      <Typography variant="caption" sx={{ color: "#d1d5db" }}>
                        0
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {summaryTotal > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              px: 2,
              py: 1.5,
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <Typography variant="body2" sx={{ color: "#6b7280" }}>
              {summaryTotal} agent{summaryTotal === 1 ? "" : "s"}
              {/* Totals come from the server and cover the whole range, so they
                  do not change as you step through pages. */}
              {summaryTotals &&
                ` · ${formatDuration(summaryTotals.productiveMs)} productive · ${formatDuration(
                  summaryTotals.breakMs
                )} break · ${summaryTotals.shifts} shifts`}
            </Typography>
            {summaryPages > 1 && (
              <Pagination
                count={summaryPages}
                page={summaryPage}
                onChange={(e, v) => setSummaryPage(v)}
                size="small"
                shape="rounded"
              />
            )}
          </Box>
        )}
      </Paper>

      {/* ── Individual shifts ────────────────────────────────────────────── */}
      <Paper sx={{ borderRadius: "12px", border: "1px solid #e5e7eb", boxShadow: "none", overflow: "hidden" }}>
        <Typography sx={{ fontWeight: 700, color: "#374151", p: 2, pb: 1 }}>Shifts</Typography>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, color: "#6b7280", bgcolor: "#f9fafb" } }}>
                    <TableCell>Date</TableCell>
                    <TableCell>Agent</TableCell>
                    <TableCell>In</TableCell>
                    <TableCell>Out</TableCell>
                    <TableCell>Breaks</TableCell>
                    <TableCell align="right">Break Time</TableCell>
                    <TableCell align="right">Productive</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!rows.length && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5, color: "#9ca3af" }}>
                        No shifts match these filters.
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r) => {
                    const meta = STATE_META[r.status] || STATE_META.off;
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{r.date}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {r.agentName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                            {r.agentUserId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {formatTime(r.clockInAt)}
                          {r.lateBy > 0 && (
                            <Tooltip title={`${formatDuration(r.lateBy)} late`}>
                              <Chip
                                label="late"
                                size="small"
                                sx={{ ml: 0.5, height: 18, fontSize: 10, bgcolor: "#fef2f2", color: "#dc2626" }}
                              />
                            </Tooltip>
                          )}
                          {r.clockInLocation && (
                            <Tooltip title={`Clocked in at ${formatCoords(r.clockInLocation)}`}>
                              <a
                                href={mapsUrl(r.clockInLocation)}
                                target="_blank"
                                rel="noreferrer"
                                style={{ marginLeft: 4, verticalAlign: "middle", color: "#0C237B" }}
                              >
                                <MapPin size={13} />
                              </a>
                            </Tooltip>
                          )}
                          <DeviceIcon device={r.clockInDevice} verb="Clocked in" />
                        </TableCell>
                        <TableCell>
                          {r.clockOutAt ? formatDateTime(r.clockOutAt) : "--"}
                          <DeviceIcon device={r.clockOutDevice} verb="Clocked out" />
                          {r.clockOutLocation && (
                            <Tooltip title={`Clocked out at ${formatCoords(r.clockOutLocation)}`}>
                              <a
                                href={mapsUrl(r.clockOutLocation)}
                                target="_blank"
                                rel="noreferrer"
                                style={{ marginLeft: 4, verticalAlign: "middle", color: "#0C237B" }}
                              >
                                <MapPin size={13} />
                              </a>
                            </Tooltip>
                          )}
                          {r.autoClockedOut && (
                            <Tooltip title={r.clockOutNote || "Closed automatically"}>
                              <Chip
                                label="auto"
                                size="small"
                                sx={{ ml: 0.5, height: 18, fontSize: 10, bgcolor: "#fffbeb", color: "#d97706" }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          {r.breaks.length ? (
                            <Tooltip
                              title={r.breaks
                                .map(
                                  (b) =>
                                    `${breakLabel(b)}: ${formatDuration(b.durationMs)}`
                                )
                                .join(" · ")}
                            >
                              <span>{r.breaks.length}</span>
                            </Tooltip>
                          ) : (
                            "0"
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ color: "#d97706" }}>
                          {formatDuration(r.totals.breakMs)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "#059669" }}>
                          {formatDuration(r.totals.productiveMs)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={meta.label}
                            sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {total > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <Typography variant="body2" sx={{ color: "#6b7280" }}>
                  Showing {(page - 1) * ROWS_PER_PAGE + 1}-
                  {Math.min(page * ROWS_PER_PAGE, total)} of {total} shifts
                </Typography>
                {pages > 1 && (
                  <Pagination
                    count={pages}
                    page={page}
                    onChange={(e, v) => setPage(v)}
                    size="small"
                    shape="rounded"
                  />
                )}
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}
